/**
 * Drawer size fields — unit check (single source of truth).
 *
 * Every drawer size field in the theme is millimetres and takes any number, so
 * a size typed in centimetres is accepted and leaves a drawer smaller than a
 * module. This file holds the one rule that spots it; each page decides where
 * its hint goes:
 *   - Drawer Builder top bar     assets/drawer-builder-legacy-inline.js
 *   - Drawer Builder onboarding  assets/drawer-onboarding-funnel.js
 *   - AI Layout Creator          assets/ai-layout-creator.js
 *
 * Loaded as a classic deferred script BEFORE those consumers
 * (sections/drawer-image.liquid, sections/ai-layout-creator.liquid). They read
 * window.mdSizeUnits when they need it and show no hint when it is missing: a
 * load-order miss or a blocked request must never break a size field.
 */
(function (root) {
  'use strict';

  // md-size-units-start
  // A drawer under 100 mm on both sides is smaller than most single modules, so
  // the size was almost certainly typed in another unit: usually centimetres
  // (43 × 33 × 5 for a 430 × 330 × 50 mm drawer, 47 × 50 for 470 × 500 mm), which
  // leaves a 1 × 1 grid that no module fits.
  // Same 100 mm rule as the eval queue (likelyUnitSlip in
  // internal-tools/lib/eval-intake.ts); keep the two in step. Pinned by
  // tests/md-size-units.test.js.
  // The height is optional. The AI Layout Creator passes the drawer's height;
  // the Drawer Builder passes length and width only, because its H field is the
  // MODULE height and must never be converted.
  const mdSizeUnits = (function () {
    const MIN_PLAUSIBLE_SIDE_MM = 100;
    // No module is lower than this, so a lower drawer fits nothing.
    const MIN_PLAUSIBLE_HEIGHT_MM = 20;
    // Deeper than this after conversion, the height was more likely millimetres already.
    const MAX_CONVERTED_HEIGHT_MM = 300;

    function positive(value) {
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
    function tidy(value) {
      return Math.round(value * 10) / 10;
    }
    function tooSmall(length, width) {
      return length < MIN_PLAUSIBLE_SIDE_MM && width < MIN_PLAUSIBLE_SIDE_MM;
    }

    // What the typed size most likely is:
    //   { kind: 'cm', size: { L, W, H } }  centimetres; `size` is the same drawer in millimetres
    //   { kind: 'small' }                   too small for a drawer, unit unknown
    //   { kind: 'height' }                  length and width are fine, the height is too low
    //   null                                plausible, or length / width not filled in yet
    function check(size) {
      const L = positive(size && size.L);
      const W = positive(size && size.W);
      const H = positive(size && size.H);
      if (!L || !W) return null;
      if (tooSmall(L, W)) {
        if (tooSmall(L * 10, W * 10)) return { kind: 'small' };
        const height = H && H * 10 <= MAX_CONVERTED_HEIGHT_MM ? H * 10 : H;
        return { kind: 'cm', size: { L: tidy(L * 10), W: tidy(W * 10), H: tidy(height) } };
      }
      if (H && H < MIN_PLAUSIBLE_HEIGHT_MM) return { kind: 'height' };
      return null;
    }

    // What was typed in a size field, as a number. A number field hands over
    // "47.5"; the builder's top bar has text fields, where "47,5" (decimal comma)
    // and "47 cm" arrive as typed. Anything that does not start with a number is 0.
    function parse(text) {
      const match = /^\s*(\d+(?:[.,]\d+)?|[.,]\d+)/.exec(String(text == null ? '' : text));
      return match ? positive(match[1].replace(',', '.')) : 0;
    }

    // "430 × 330 × 50": the unit is part of the translated sentence.
    function label(size) {
      return [size.L, size.W, size.H].filter((side) => positive(side)).map((side) => String(tidy(side))).join(' × ');
    }

    // A size and its unit stay on one line: "430 × 330 × 50 mm" never wraps mid-way
    // on a narrow screen. Done here so the locale files carry no invisible spaces;
    // the no-break spaces are written as escapes so this file carries none either.
    function noBreak(text) {
      return String(text).replace(/(\d) × (?=\d)/g, '$1\u00a0×\u00a0').replace(/(\d) (?=mm(?![a-z]))/g, '$1\u00a0');
    }

    return { MIN_PLAUSIBLE_SIDE_MM, MIN_PLAUSIBLE_HEIGHT_MM, MAX_CONVERTED_HEIGHT_MM, check, parse, label, noBreak };
  })();
  // md-size-units-end

  root.mdSizeUnits = root.mdSizeUnits || mdSizeUnits;
})(window);
